import React, { useEffect, useMemo, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";
import { useTranslation } from "react-i18next";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import DirectionsBusIcon from "@material-ui/icons/DirectionsBus";
import { makeStyles } from "@material-ui/core/styles";
import * as API_KEYS from "../../constants/apiKeys";

const TRACE_POLYLINE_COLOR = "#8e24aa";
const TRACE_MIN_DISTANCE_METERS = 25;
const TRACE_MIN_TIME_SECONDS = 45;
const MAX_TRACE_MARKERS = 8;
const TRACE_MARKER_MIN_DISTANCE_METERS = 65;
const TRACE_FIT_BOUNDS_PADDING = 96;
const TRACE_FIT_BOUNDS_MIN_DISTANCE_METERS = 120;

const useStyles = makeStyles((theme) => ({
  mapWrapper: {
    width: "100%",
    height: "100%",
    minHeight: 360,
    borderRadius: 4,
    overflow: "hidden",
    border: "1px solid #d9dde2",
    boxSizing: "border-box",

    "@media (max-width:600px)": {
      minHeight: 300,
    },
  },

  mainMarkerAnchor: {
    transform: "translate(-50%, -50%)",
  },

  mainMarker: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "#d71920",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
    border: "3px solid #ffffff",
  },

  mainMarkerIcon: {
    color: "#ffffff",
    fontSize: 28,
  },

  busMarkerAnchor: {
    transform: "translate(-50%, -50%)",
  },

  busMarker: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.28)",
    border: "3px solid #009fe3",
  },

  busMarkerIcon: {
    fontSize: 26,
  },

  traceMarker: {
    transform: "translate(-50%, -50%)",
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: TRACE_POLYLINE_COLOR,
    boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
    border: "2px solid #ffffff",
  },

  traceMarkerIcon: {
    display: "none",
  },

  traceStartMarker: {
    transform: "translate(-50%, -50%)",
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: TRACE_POLYLINE_COLOR,
    boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
    border: "2px solid #ffffff",
  },

  tracePointMarker: {
    transform: "translate(-50%, -50%)",
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: TRACE_POLYLINE_COLOR,
    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
    border: "1px solid #ffffff",
  },

  selectedTraceMarkerAnchor: {
    transform: "translate(-50%, -50%)",
    zIndex: 4,
    pointerEvents: "none",
  },

  selectedTraceMarker: {
    position: "relative",
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "#ffffff",
    border: `4px solid ${TRACE_POLYLINE_COLOR}`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.30)",
    boxSizing: "border-box",
    animation: "$selectedTracePulse 220ms ease-out",
  },

  selectedTraceMarkerCore: {
    position: "absolute",
    left: "50%",
    top: "50%",
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: TRACE_POLYLINE_COLOR,
    transform: "translate(-50%, -50%)",
  },

  selectedTraceMarkerLabel: {
    position: "absolute",
    left: "50%",
    bottom: "calc(100% + 5px)",
    transform: "translateX(-50%)",
    padding: "2px 6px",
    borderRadius: 999,
    background: TRACE_POLYLINE_COLOR,
    color: "#ffffff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.25,
    whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(0,0,0,0.24)",
    pointerEvents: "none",
    [theme.breakpoints.down("xs")]: {
      fontSize: 10,
      padding: "1px 5px",
      bottom: "calc(100% + 4px)",
    },
  },

  "@keyframes selectedTracePulse": {
    "0%": {
      transform: "scale(0.82)",
      opacity: 0.72,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
}));

const isValidCoordinate = (value) => value !== null && value !== undefined && !Number.isNaN(Number(value));

const hasTimezoneInfo = (dateText) => /Z$|[+-]\d{2}:?\d{2}$/.test(dateText);

const formatMadridTime = (value) => {
  if (!value) {
    return "";
  }

  const dateText = String(value);
  const normalizedDateText = hasTimezoneInfo(dateText) ? dateText : `${dateText}Z`;
  const parsedDate = new Date(normalizedDateText);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toLocaleTimeString("es-ES", {
    timeZone: "Europe/Madrid",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const normalizePoint = (point) => {
  if (!point || !isValidCoordinate(point.latitude) || !isValidCoordinate(point.longitude)) {
    return null;
  }

  return {
    latitude: Number(point.latitude),
    longitude: Number(point.longitude),
    position_at: point.position_at,
  };
};

const toRadians = (degrees) => (degrees * Math.PI) / 180;

const getDistanceMeters = (pointA, pointB) => {
  if (!pointA || !pointB) {
    return 0;
  }

  const earthRadiusMeters = 6371000;
  const deltaLatitude = toRadians(pointB.latitude - pointA.latitude);
  const deltaLongitude = toRadians(pointB.longitude - pointA.longitude);
  const latitudeA = toRadians(pointA.latitude);
  const latitudeB = toRadians(pointB.latitude);

  const haversine = Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) + Math.cos(latitudeA) * Math.cos(latitudeB) * Math.sin(deltaLongitude / 2) * Math.sin(deltaLongitude / 2);

  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

const getTraceSpanMeters = (points) => {
  if (!Array.isArray(points) || points.length < 2) {
    return 0;
  }

  return points.reduce((maxDistance, point) => {
    const distanceFromStart = getDistanceMeters(points[0], point);
    return Math.max(maxDistance, distanceFromStart);
  }, 0);
};

const parseDate = (value) => {
  if (!value) {
    return null;
  }

  const dateText = String(value);
  const normalizedDateText = /Z$|[+-]\d{2}:?\d{2}$/.test(dateText) ? dateText : `${dateText}Z`;
  const parsedDate = new Date(normalizedDateText);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getSecondsBetween = (pointA, pointB) => {
  const dateA = parseDate(pointA?.position_at);
  const dateB = parseDate(pointB?.position_at);

  if (!dateA || !dateB) {
    return 0;
  }

  return Math.abs(dateB.getTime() - dateA.getTime()) / 1000;
};

const hasSameCoordinates = (pointA, pointB) =>
  pointA && pointB && Number(pointA.latitude).toFixed(6) === Number(pointB.latitude).toFixed(6) && Number(pointA.longitude).toFixed(6) === Number(pointB.longitude).toFixed(6);

const isSameMapPoint = (pointA, pointB) =>
  pointA &&
  pointB &&
  Number(pointA.latitude).toFixed(6) === Number(pointB.latitude).toFixed(6) &&
  Number(pointA.longitude).toFixed(6) === Number(pointB.longitude).toFixed(6) &&
  (pointA.position_at || "") === (pointB.position_at || "");

const simplifyTrace = (points, minDistanceMeters = TRACE_MIN_DISTANCE_METERS, minTimeSeconds = TRACE_MIN_TIME_SECONDS) => {
  if (!Array.isArray(points) || points.length <= 2) {
    return points || [];
  }

  const simplified = [points[0]];
  let lastKeptPoint = points[0];

  points.slice(1, -1).forEach((point) => {
    if (hasSameCoordinates(point, lastKeptPoint)) {
      return;
    }

    const distanceMeters = getDistanceMeters(lastKeptPoint, point);
    const secondsBetween = getSecondsBetween(lastKeptPoint, point);

    if (distanceMeters >= minDistanceMeters || secondsBetween >= minTimeSeconds) {
      simplified.push(point);
      lastKeptPoint = point;
    }
  });

  const lastPoint = points[points.length - 1];

  if (!hasSameCoordinates(lastPoint, simplified[simplified.length - 1])) {
    simplified.push(lastPoint);
  }

  return simplified;
};

const getTraceMarkerPoints = (points, maxMarkers = MAX_TRACE_MARKERS, minMarkerDistanceMeters = TRACE_MARKER_MIN_DISTANCE_METERS) => {
  if (!Array.isArray(points) || points.length <= 1) {
    return [];
  }

  const markerCandidates = points.slice(0, -1); // excluimos último: ya lo representa el corazón R100

  if (markerCandidates.length <= 1) {
    return markerCandidates;
  }

  const markerPoints = [markerCandidates[0]];
  let lastMarkerPoint = markerCandidates[0];

  markerCandidates.slice(1).forEach((point) => {
    if (hasSameCoordinates(point, lastMarkerPoint)) {
      return;
    }

    const distanceMeters = getDistanceMeters(lastMarkerPoint, point);

    if (distanceMeters >= minMarkerDistanceMeters) {
      markerPoints.push(point);
      lastMarkerPoint = point;
    }
  });

  if (markerPoints.length <= maxMarkers) {
    return markerPoints;
  }

  const step = Math.ceil(markerPoints.length / maxMarkers);

  return markerPoints.filter((point, index) => index === 0 || index === markerPoints.length - 1 || index % step === 0);
};

const OperationalGisMap = ({ position, trace = [], showTrace = false, traceDisplayPoints = [], selectedTracePoint, busPosition, busStatusInfo, onMapApiReady, t }) => {
  const classes = useStyles();
  const { i18n } = useTranslation();
  const googleMapsLanguage = (i18n.language || "es").split("-")[0];
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const tracePolylineRef = useRef(null);

  const [isMapApiReady, setIsMapApiReady] = useState(false);

  const mainPosition = useMemo(() => normalizePoint(position), [position]);
  const mainPositionKey = mainPosition ? `${mainPosition.latitude}-${mainPosition.longitude}-${mainPosition.position_at || ""}` : "";
  const normalizedBusPosition = useMemo(() => normalizePoint(busPosition), [busPosition]);

  const normalizedSelectedTracePoint = useMemo(() => normalizePoint(selectedTracePoint), [selectedTracePoint]);

  const selectedTracePointPosition = showTrace && normalizedSelectedTracePoint ? normalizedSelectedTracePoint : null;

  const selectedTracePointTime = selectedTracePointPosition?.position_at ? formatMadridTime(selectedTracePointPosition.position_at) : "";

  const shouldShowSelectedTraceMarker = selectedTracePointPosition && !isSameMapPoint(selectedTracePointPosition, mainPosition);

  const normalizedTraceDisplayPoints = useMemo(() => {
    if (!Array.isArray(traceDisplayPoints)) {
      return [];
    }

    return traceDisplayPoints.map(normalizePoint).filter(Boolean);
  }, [traceDisplayPoints]);

  const normalizedTrace = useMemo(() => {
    if (!Array.isArray(trace)) {
      return [];
    }

    return trace
      .map(normalizePoint)
      .filter(Boolean)
      .filter((tracePoint, index, allPoints) => index === 0 || !hasSameCoordinates(tracePoint, allPoints[index - 1]));
  }, [trace]);

  const simplifiedTrace = useMemo(() => simplifyTrace(normalizedTrace), [normalizedTrace]);

  const traceMarkerPoints = useMemo(() => {
    if (!showTrace) {
      return [];
    }

    return normalizedTraceDisplayPoints.length > 0 ? normalizedTraceDisplayPoints : getTraceMarkerPoints(simplifiedTrace);
  }, [showTrace, normalizedTraceDisplayPoints, simplifiedTrace]);

  useEffect(() => {
    if (tracePolylineRef.current) {
      tracePolylineRef.current.setMap(null);
      tracePolylineRef.current = null;
    }
    if (!showTrace || !isMapApiReady || !mapRef.current || !mapsRef.current || normalizedTrace.length < 2) {
      return undefined;
    }

    const path = normalizedTrace.map((tracePoint) => ({
      lat: tracePoint.latitude,
      lng: tracePoint.longitude,
    }));

    const bounds = new mapsRef.current.LatLngBounds();

    path.forEach((point) => {
      bounds.extend(point);
    });

    if (mainPosition) {
      bounds.extend({
        lat: mainPosition.latitude,
        lng: mainPosition.longitude,
      });
    }

    const traceSpanMeters = getTraceSpanMeters(simplifiedTrace);

    if (traceSpanMeters >= TRACE_FIT_BOUNDS_MIN_DISTANCE_METERS) {
      mapRef.current.fitBounds(bounds, TRACE_FIT_BOUNDS_PADDING);
    }

    tracePolylineRef.current = new mapsRef.current.Polyline({
      path,
      map: mapRef.current,
      strokeColor: TRACE_POLYLINE_COLOR,
      strokeOpacity: 0.75,
      strokeWeight: 3,
      geodesic: false,
    });

    return () => {
      if (tracePolylineRef.current) {
        tracePolylineRef.current.setMap(null);
        tracePolylineRef.current = null;
      }
    };
  }, [showTrace, normalizedTrace, simplifiedTrace, isMapApiReady, mainPositionKey]);

  if (!mainPosition) {
    return null;
  }

  const center = {
    lat: mainPosition.latitude,
    lng: mainPosition.longitude,
  };

  return (
    <div className={classes.mapWrapper}>
      <GoogleMapReact
        key={`operational-gis-map-${googleMapsLanguage}`}
        bootstrapURLKeys={{
          key: API_KEYS.MAPS_API_KEY,
          language: googleMapsLanguage,
        }}
        center={center}
        defaultZoom={showTrace ? 15 : 16}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => {
          mapRef.current = map;
          mapsRef.current = maps;
          setIsMapApiReady(true);

          if (typeof onMapApiReady === "function") {
            onMapApiReady({ map, maps });
          }
        }}
        options={(maps) => ({
          mapTypeId: "roadmap",
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: maps.MapTypeControlStyle.DROPDOWN_MENU,
            position: maps.ControlPosition.TOP_LEFT,
            mapTypeIds: ["roadmap", "hybrid"],
          },
          streetViewControl: false,
          fullscreenControl: true,
        })}
      >
        {traceMarkerPoints.map((tracePoint, index) => {
          const isStartPoint = index === 0;
          const isSelectedTraceMarker = isSameMapPoint(tracePoint, selectedTracePointPosition);

          if (isSelectedTraceMarker) {
            return null;
          }

          return (
            <div
              key={`trace-point-${tracePoint.latitude}-${tracePoint.longitude}-${tracePoint.position_at || index}`}
              lat={tracePoint.latitude}
              lng={tracePoint.longitude}
              className={isStartPoint ? classes.traceStartMarker : classes.tracePointMarker}
              title={isStartPoint ? `Inicio histórico R100 - ${tracePoint.position_at || ""}` : tracePoint.position_at || ""}
            >
              <FiberManualRecordIcon className={classes.traceMarkerIcon} />
            </div>
          );
        })}

        {shouldShowSelectedTraceMarker && (
          <div key="selected-trace-marker" lat={selectedTracePointPosition.latitude} lng={selectedTracePointPosition.longitude} className={classes.selectedTraceMarkerAnchor}>
            <div
              className={classes.selectedTraceMarker}
              title={`${t?.("devices.tracking.config.status.operational_map.history.selected_marker", "Posición seleccionada")}${
                selectedTracePointPosition.position_at ? ` · ${selectedTracePointPosition.position_at}` : ""
              }`}
            >
              {selectedTracePointTime && <span className={classes.selectedTraceMarkerLabel}>{selectedTracePointTime}</span>}
              <span className={classes.selectedTraceMarkerCore} />
            </div>
          </div>
        )}

        {normalizedBusPosition && (
          <div
            key={`bus-${normalizedBusPosition.latitude}-${normalizedBusPosition.longitude}`}
            lat={normalizedBusPosition.latitude}
            lng={normalizedBusPosition.longitude}
            className={classes.busMarkerAnchor}
          >
            <div className={classes.busMarker} style={{ borderColor: busStatusInfo?.iconColor || "#009fe3" }} title={normalizedBusPosition.position_at || ""}>
              <DirectionsBusIcon
                className={busStatusInfo?.iconClass ? classes[busStatusInfo.iconClass] : classes.busMarkerIcon}
                style={{
                  color: busStatusInfo?.iconColor || "#009fe3",
                  fontSize: 26,
                }}
              />
            </div>
          </div>
        )}

        <div key={`r100-${mainPosition.latitude}-${mainPosition.longitude}`} lat={mainPosition.latitude} lng={mainPosition.longitude} className={classes.mainMarkerAnchor}>
          <div className={classes.mainMarker} title={mainPosition.position_at || ""}>
            <FavoriteBorderIcon className={classes.mainMarkerIcon} />
          </div>
        </div>
      </GoogleMapReact>
    </div>
  );
};

export default OperationalGisMap;
